import type { PageHeader, ViewLayout } from '@crowdex/plugin-contracts';
import { Button } from '@crowdex/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@crowdex/ui/components/dropdown-menu';
import { cn } from '@crowdex/ui/lib/utils';
import {
  ChevronDown,
  ChevronLeft,
  Ellipsis,
  type LucideIcon,
  Maximize2,
  PanelRight,
  Settings,
  X,
} from 'lucide-react';
import { type ReactNode, useCallback, useState } from 'react';
import { ItemIcon } from '@/components/item-icon';
import { pluginIcon } from '@/features/plugins/plugin-icons';
import { pluginPagePath } from '@/lib/plugins/plugin-sections';
import { navigateApp } from '@/vite/navigation-store';

/**
 * The frame the host draws around every container of an extension (docs/adr-plugin-model-
 * uniformization.md R8, the extensions proposal's "Le cadre"): its home, an item full screen, a
 * tab docked in a conversation's side panel, an embedded web app. One 56 px bar: on the left the
 * chevron (on a level below the home), the icon and the title (the item's on an item), the origin
 * ("From <assistant>") and the status; on the right the view's actions, one primary at most, and
 * the ⋯ menu (the type's actions, open elsewhere, Settings, Close). At the right of the content,
 * the inspector when the view opened one (`page.inspector.open`), 320 px. Views draw only their
 * content.
 */

/** An action of the bar: the host's (an item's, "New …") or the view's (`page.header.set`). */
export type FrameAction = {
  id: string;
  label: string;
  icon?: LucideIcon | undefined;
  primary?: boolean | undefined;
  shortcut?: string | undefined;
  disabled?: boolean | undefined;
  /** Choices: the action opens a menu of them. */
  menu?: readonly { id: string; label: string; disabled?: boolean | undefined }[] | undefined;
  /** Runs it (with the choice's id for a menu). */
  onSelect(id: string): void;
};

/** An entry of the ⋯ menu above "open elsewhere": the type's actions. */
export type FrameMenuItem = {
  id: string;
  label: string;
  icon?: LucideIcon | undefined;
  disabled?: boolean | undefined;
  onSelect(): void;
};

/** Who made what the frame shows: drawn as "From <assistant>", with the assistant's colour. */
export type FrameOrigin = {
  name: string;
  color?: string | null | undefined;
  onOpen?: (() => void) | undefined;
};

export type PluginFrameProps = {
  plugin: string;
  layout: ViewLayout;
  icon: LucideIcon;
  iconSrc?: string | undefined;
  title: string;
  /** Up a level (shown on a level below the home): the chevron, as ⌘[. */
  onBack?: (() => void) | undefined;
  /** What the chevron goes back to, for its label ("Back to Sheets"). */
  backLabel?: string | undefined;
  origin?: FrameOrigin | null | undefined;
  status?: string | undefined;
  /** Docked: the extension's sections, as a picker after the title. */
  picker?: ReactNode;
  actions?: readonly FrameAction[] | undefined;
  /** The host's own controls that are not plain actions (a type's action with its state). */
  controls?: ReactNode;
  menu?: readonly FrameMenuItem[] | undefined;
  /** Full screen → docked ("Open in a conversation"), docked → full screen. */
  openElsewhere?: { label: string; onSelect(): void } | undefined;
  onClose?: (() => void) | undefined;
  /** Over the content, under the bar (the floating composer of an item). */
  overlay?: ReactNode;
  /** The view's inspector (`page.inspector.open`), in a 320 px column at the right. */
  inspector?: ReactNode;
  children: ReactNode;
  className?: string | undefined;
};

/** The view's header (`page.header.set`) as actions of the bar; a click reaches it by id. */
export function viewHeaderActions(
  header: PageHeader | null,
  onAction: (id: string) => void,
): FrameAction[] {
  return (header?.actions ?? []).map((action) => ({
    id: action.id,
    label: action.label,
    ...(action.icon === undefined ? {} : { icon: pluginIcon(action.icon) }),
    primary: action.primary === true,
    shortcut: action.shortcut,
    disabled: action.disabled,
    ...(action.menu === undefined ? {} : { menu: action.menu }),
    onSelect: onAction,
  }));
}

/**
 * The view's header and the clicks on its actions, held by the container that frames it: the
 * frame reads `header`, the view's context carries `action` (one more `sequence` per click).
 */
export function useViewHeader(): {
  header: PageHeader | null;
  setHeader(header: PageHeader | null): void;
  action: { id: string; sequence: number } | null;
  emit(id: string): void;
} {
  const [header, setHeader] = useState<PageHeader | null>(null);
  const [action, setAction] = useState<{ id: string; sequence: number } | null>(null);
  const emit = useCallback(
    (id: string) => setAction((previous) => ({ id, sequence: (previous?.sequence ?? 0) + 1 })),
    [],
  );
  return { header, setHeader, action, emit };
}

/** The bar's primary action: the first that asks for it (one at most is drawn as primary). */
function withOnePrimary(actions: readonly FrameAction[]): FrameAction[] {
  let primary = false;
  return actions.map((action) => {
    if (action.primary !== true) return action;
    if (primary) return { ...action, primary: false };
    primary = true;
    return action;
  });
}

/** An action of the bar, as the frame draws it (a docked artifact's header draws them too). */
export function FrameActionButton({ action }: { action: FrameAction }) {
  const Icon = action.icon;
  const content = (
    <>
      {Icon !== undefined && <Icon className="size-3.5" />}
      {action.label}
      {action.menu !== undefined && <ChevronDown className="size-3.5" />}
    </>
  );
  const title = action.shortcut === undefined ? undefined : `${action.label} (${action.shortcut})`;
  const variant = action.primary ? 'default' : 'ghost';
  if (action.menu !== undefined)
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            data-frame-action={action.id}
            disabled={action.disabled}
            size="sm"
            title={title}
            type="button"
            variant={variant}
          >
            {content}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {action.menu.map((choice) => (
            <DropdownMenuItem
              disabled={choice.disabled}
              key={choice.id}
              onSelect={() => action.onSelect(choice.id)}
            >
              {choice.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  return (
    <Button
      data-frame-action={action.id}
      data-primary={action.primary ? '' : undefined}
      disabled={action.disabled}
      onClick={() => action.onSelect(action.id)}
      size="sm"
      title={title}
      type="button"
      variant={variant}
    >
      {content}
    </Button>
  );
}

/** "From <assistant>", with its colour; a click opens its channel. */
export function OriginChip({ origin }: { origin: FrameOrigin }) {
  const body = (
    <>
      <span
        aria-hidden
        className="size-2 shrink-0 rounded-[3px] bg-muted-foreground"
        style={origin.color ? { backgroundColor: origin.color } : undefined}
      />
      <span className="truncate">From {origin.name}</span>
    </>
  );
  const className =
    'type-meta inline-flex min-w-0 max-w-56 shrink items-center gap-1.5 rounded-control px-2 py-1 text-muted-foreground';
  return origin.onOpen === undefined ? (
    <span className={className} data-frame-origin>
      {body}
    </span>
  ) : (
    <button
      className={cn(className, 'hover:bg-control hover:text-foreground')}
      data-frame-origin
      onClick={origin.onOpen}
      type="button"
    >
      {body}
    </button>
  );
}

/** The inspector's column: 320 px at the right of a view, the view's second document in it. */
export function PluginInspector({ title, children }: { title: string; children: ReactNode }) {
  return (
    <aside
      aria-label={`${title} inspector`}
      className="relative w-80 shrink-0 border-hairline border-l"
      data-plugin-frame-inspector
    >
      {children}
    </aside>
  );
}

export function PluginFrame(props: PluginFrameProps) {
  const actions = withOnePrimary(props.actions ?? []);
  const menu = props.menu ?? [];
  return (
    <section
      aria-label={props.title}
      className={cn('relative flex h-full min-h-0 min-w-0 flex-col', props.className)}
      data-plugin-frame={props.plugin}
      data-plugin-layout={props.layout}
    >
      <div
        className="flex h-14 min-w-0 shrink-0 items-center gap-2 border-hairline border-b px-3"
        data-plugin-frame-bar
      >
        {props.onBack !== undefined && (
          <Button
            aria-label={props.backLabel === undefined ? 'Back' : `Back to ${props.backLabel}`}
            className="shrink-0"
            onClick={props.onBack}
            size="icon-sm"
            type="button"
            variant="ghost"
          >
            <ChevronLeft className="size-4" />
          </Button>
        )}
        <span aria-hidden className="flex size-6 shrink-0 items-center justify-center">
          <ItemIcon className="size-4" icon={props.icon} src={props.iconSrc} />
        </span>
        <h1 className="type-ui min-w-0 shrink truncate font-semibold" title={props.title}>
          {props.title}
        </h1>
        {props.picker}
        {props.origin !== undefined && props.origin !== null && (
          <OriginChip origin={props.origin} />
        )}
        {props.status !== undefined && (
          <span className="type-meta shrink-0 text-muted-foreground" data-frame-status>
            {props.status}
          </span>
        )}
        <span className="min-w-0 flex-1" />
        <div className="flex min-w-0 shrink items-center justify-end gap-1.5 overflow-hidden">
          {props.controls}
          {actions.map((action) => (
            <FrameActionButton action={action} key={action.id} />
          ))}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              aria-label={`More for ${props.title}`}
              className="shrink-0"
              size="icon-sm"
              type="button"
              variant="ghost"
            >
              <Ellipsis className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-52">
            {menu.map((item) => {
              const Icon = item.icon;
              return (
                <DropdownMenuItem disabled={item.disabled} key={item.id} onSelect={item.onSelect}>
                  {Icon !== undefined && <Icon className="size-4" />}
                  {item.label}
                </DropdownMenuItem>
              );
            })}
            {menu.length > 0 && <DropdownMenuSeparator />}
            {props.openElsewhere !== undefined && (
              <>
                <DropdownMenuItem onSelect={props.openElsewhere.onSelect}>
                  {props.layout === 'docked' ? (
                    <Maximize2 className="size-4" />
                  ) : (
                    <PanelRight className="size-4" />
                  )}
                  {props.openElsewhere.label}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem
              onSelect={() => navigateApp(pluginPagePath('installed', props.plugin))}
            >
              <Settings className="size-4" />
              Settings
            </DropdownMenuItem>
            {props.onClose !== undefined && (
              <DropdownMenuItem onSelect={props.onClose}>
                <X className="size-4" />
                Close
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="flex min-h-0 flex-1">
        <div className="relative min-h-0 min-w-0 flex-1" data-plugin-frame-content>
          {props.children}
        </div>
        {props.inspector !== undefined && props.inspector !== null && (
          <PluginInspector title={props.title}>{props.inspector}</PluginInspector>
        )}
      </div>
      {props.overlay}
    </section>
  );
}
